import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

import { isAuthRoute } from '@/lib/auth/isAuthRoute';
import { installAuthTripwire } from '@/lib/debug/auth-tripwire';

// Use 'any' for the generic types to avoid complex type mismatches
type BrowserSupabaseClient = SupabaseClient<any, any, any>;

// ─────────────────────────────────────────────────────────────────────────────
// SINGLETON STATE
// ─────────────────────────────────────────────────────────────────────────────
let client: BrowserSupabaseClient | undefined;
let publicClient: BrowserSupabaseClient | undefined;
let authListenerUnsubscribe: (() => void) | undefined;

// Circuit breaker state - prevents refresh storms
let isRecovering = false;
let lastRecoveryAttempt = 0;
let recoveryAttemptCount = 0;

// Constants
const RECOVERY_COOLDOWN_MS = 60000; // 1 minute between recovery attempts
const MAX_RECOVERY_ATTEMPTS = 2; // Max attempts per page load
const RECOVERY_ATTEMPT_RESET_MS = 300000; // Reset counter after 5 minutes

function getPathnameSafe(): string {
  if (typeof window === 'undefined') return '';
  return window.location?.pathname ?? '';
}

function stopAutoRefreshOnClient(supabaseClient: BrowserSupabaseClient | undefined): void {
  if (!supabaseClient) return;
  const authClient = supabaseClient.auth as unknown as { stopAutoRefresh?: () => void };
  if (typeof authClient.stopAutoRefresh === 'function') {
    authClient.stopAutoRefresh();
  }
}

/**
 * Canonical browser singleton getter for Supabase client usage in frontend code.
 */
export function getSupabaseBrowser(): BrowserSupabaseClient {
  const pathname = getPathnameSafe();
  const onAuthRoute = isAuthRoute(pathname);

  if (onAuthRoute) {
    stopAutoRefreshOnClient(client);
    if (process.env.NODE_ENV === 'development') {
      console.error('[Supabase Client] getSupabaseBrowser blocked on auth route', {
        pathname,
        stack: new Error().stack,
      });
    }
    return getSupabaseBrowserPublic();
  }

  if (client) {
    return client;
  }

  client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        // CRITICAL: Server handles OAuth callback, not the browser
        detectSessionInUrl: false,
        flowType: 'pkce',
        // Disable refresh and persistence on auth routes to prevent storms.
        autoRefreshToken: true,
        persistSession: true,
      },
    }
  );

  if (typeof window !== 'undefined') {
    installAuthTripwire();
    setupAuthListener(client, onAuthRoute);
  }

  return client;
}

/**
 * Public/auth-page client that avoids auto refresh + session persistence.
 * Used on /login and /auth/callback UI surfaces only.
 */
export function getSupabaseBrowserPublic(): BrowserSupabaseClient {
  if (publicClient) {
    return publicClient;
  }

  publicClient = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
        flowType: 'pkce',
      },
    }
  );

  return publicClient;
}

/**
 * Clear the client instance (for testing or forced logout)
 */
export function clearSupabaseBrowser(): void {
  if (authListenerUnsubscribe) {
    authListenerUnsubscribe();
    authListenerUnsubscribe = undefined;
  }
  client = undefined;
  publicClient = undefined;
  isRecovering = false;
  recoveryAttemptCount = 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Set up auth state change listener.
 * Only handles explicit SIGNED_OUT events and token refresh errors.
 */
function setupAuthListener(supabaseClient: BrowserSupabaseClient, onAuthRoute: boolean): void {
  if (onAuthRoute) return;

  const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
    async (event, session) => {
      const pathname = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
      const shouldTrace = process.env.NEXT_PUBLIC_AUTH_DIAGNOSTICS === '1';
      if (shouldTrace) {
        console.log('[MH Auth Tripwire] auth event', {
          event,
          hasSession: Boolean(session),
          pathname,
        });
      }

      // Handle explicit sign out
      if (event === 'SIGNED_OUT') {
        console.log('[Supabase Client] User signed out');
        return;
      }

      // Handle token refresh errors - this is where storms can originate
      if (event === 'TOKEN_REFRESHED' && !session) {
        console.warn('[Supabase Client] Token refresh failed (no session returned)');
        await handleBrokenSession(supabaseClient, 'token_refresh_failed');
        return;
      }

      // Log successful initial session for debugging
      if (event === 'INITIAL_SESSION' && session) {
        console.log('[Supabase Client] Initial session loaded');
      }
    }
  );

  authListenerUnsubscribe = () => subscription.unsubscribe();

  // Intercept fetch to detect refresh token errors
  interceptRefreshErrors(supabaseClient);
}

/**
 * Intercept network errors related to token refresh.
 * This catches 400 and 429 errors from /token endpoint that indicate broken sessions
 * or rate limiting that would cause infinite retry loops.
 */
function interceptRefreshErrors(supabaseClient: BrowserSupabaseClient): void {
  if (typeof window === 'undefined') return;
  if ((window as Window & { __MH_REFRESH_INTERCEPTOR_INSTALLED__?: boolean }).__MH_REFRESH_INTERCEPTOR_INSTALLED__) {
    return;
  }

  (window as Window & { __MH_REFRESH_INTERCEPTOR_INSTALLED__?: boolean }).__MH_REFRESH_INTERCEPTOR_INSTALLED__ = true;
  const originalFetch = window.fetch;

  // Track 429 errors to detect refresh storms
  let rateLimitCount = 0;
  let lastRateLimitTime = 0;
  const RATE_LIMIT_WINDOW_MS = 10000; // 10 second window
  const RATE_LIMIT_THRESHOLD = 2; // Trigger after 2 rate limits in window

  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);

    // Check if this is a failed token refresh request
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url;
    if (url?.includes('/auth/v1/token') && url?.includes('grant_type=refresh_token')) {
      // Handle 429 (Too Many Requests) - CRITICAL for stopping refresh storms
      if (response.status === 429) {
        const now = Date.now();

        // Reset counter if outside window
        if (now - lastRateLimitTime > RATE_LIMIT_WINDOW_MS) {
          rateLimitCount = 0;
        }

        rateLimitCount++;
        lastRateLimitTime = now;

        console.warn('[Supabase Client] Token refresh rate limited (429)', {
          count: rateLimitCount,
          threshold: RATE_LIMIT_THRESHOLD,
        });

        // Trigger circuit breaker after threshold to stop the storm
        if (rateLimitCount >= RATE_LIMIT_THRESHOLD) {
          console.error('[Supabase Client] Refresh storm detected - triggering circuit breaker');
          // Handle asynchronously to not block the response
          setTimeout(() => handleBrokenSession(supabaseClient, 'refresh_rate_limited_429'), 0);
        }
      }

      // Handle 400 (Bad Request) - broken tokens
      if (response.status === 400) {
        // Clone response to read body without consuming it
        const clonedResponse = response.clone();
        try {
          const body = await clonedResponse.json();
          const errorMessage = body?.error_description || body?.error || '';

          if (
            errorMessage.includes('refresh_token_not_found') ||
            errorMessage.includes('invalid_grant') ||
            errorMessage.includes('invalid refresh token')
          ) {
            console.warn('[Supabase Client] Detected broken refresh token:', errorMessage);
            // Handle asynchronously to not block the response
            setTimeout(() => handleBrokenSession(supabaseClient, errorMessage), 0);
          }
        } catch {
          // Ignore JSON parse errors
        }
      }
    }

    return response;
  };
}

/**
 * Handle a broken session by clearing local state and redirecting to login.
 * Includes circuit breaker to prevent storms.
 */
async function handleBrokenSession(supabaseClient: BrowserSupabaseClient, reason: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const now = Date.now();

  // Reset attempt counter if enough time has passed
  if (now - lastRecoveryAttempt > RECOVERY_ATTEMPT_RESET_MS) {
    recoveryAttemptCount = 0;
  }

  // Circuit breaker: check cooldown
  if (now - lastRecoveryAttempt < RECOVERY_COOLDOWN_MS) {
    console.log('[Supabase Client] Recovery cooldown active, skipping');
    return;
  }

  // Circuit breaker: check attempt limit
  if (recoveryAttemptCount >= MAX_RECOVERY_ATTEMPTS) {
    console.log('[Supabase Client] Max recovery attempts reached, skipping');
    return;
  }

  // Prevent concurrent recovery
  if (isRecovering) {
    console.log('[Supabase Client] Recovery already in progress, skipping');
    return;
  }

  const currentPath = window.location.pathname;
  if (isAuthRoute(currentPath)) {
    console.log('[Supabase Client] Already on auth page, skipping recovery');
    return;
  }

  isRecovering = true;
  lastRecoveryAttempt = now;
  recoveryAttemptCount++;

  console.log('[Supabase Client] Initiating session recovery', {
    reason,
    attempt: recoveryAttemptCount,
  });

  try {
    // Sign out locally only - don't make network calls that could fail
    await supabaseClient.auth.signOut({ scope: 'local' });

    // Clear localStorage items related to Supabase
    clearLocalStorageAuth();

    // Redirect to the clear endpoint for server-side cookie cleanup
    window.location.href = '/auth/clear';
  } catch (error) {
    console.error('[Supabase Client] Error during session recovery:', error);
    // Force redirect even on error
    window.location.href = '/login?error=session_recovery_failed';
  } finally {
    // Reset after a delay (in case redirect doesn't happen)
    setTimeout(() => {
      isRecovering = false;
    }, 5000);
  }
}

/**
 * Clear all Supabase-related items from localStorage
 */
function clearLocalStorageAuth(): void {
  if (typeof window === 'undefined') return;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  });

  if (keysToRemove.length > 0) {
    console.log('[Supabase Client] Cleared localStorage:', keysToRemove);
  }
}
